import ElevatedPaper from "../../../ElevatedPaper";

import { Box, Button } from "@mui/material";
import { GridRowSelectionModel, GridSortItem } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { STATUS } from "../../../../constants/AutocompleteOptions";
import { useDebounce } from "../../../../hooks/use-debounce";
import { useReduceDisclosure } from "../../../../hooks/use-reduce-disclosure";
import SendBulkEmailModal from "../../../../pages/app/provider/communications/email/SendBulkEmailModal";
import { useFeeScheduleArchive } from "../../../../queries/fee-scheduling/use-fee-schedule-archive";
import { useFeeScheduleList } from "../../../../queries/fee-scheduling/use-fee-schedule-list";
import { useFeeScheduleStatus } from "../../../../queries/fee-scheduling/use-fee-schedule-status";
import { useFeeCptHcpcsCodeList } from "../../../../queries/fee-scheduling/use-medical-cpt-hcpcs-code";
import { useProviderListV2 } from "../../../../queries/use-provider-list-v2";
import {
  DEFAULT_VALUE_LABEL_LIST,
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
  transformMultiSelectFrontEndVal,
} from "../../../../utils/utils";
import AsyncSelectScroll from "../../../AsyncSelectScroll";
import FilterMaskMultiSelectScroll from "./FilterMaskMultiSelectScroll";
import CustomDataGridV2 from "../../../CustomDataGridV2";
import { FilterSelect } from "../../../FilterSelect";
import Flex from "../../../Flex";
import { GlobalTableHeightWrapper } from "../../../GlobalTableHeightWrapper";
import RenderWithTooltip from "../../../RenderWithTooltip";
import Text from "../../../Text";
import ToggleSwitch from "../../../ToggleSwitch";
import ActionMenu from "../../ActionMenu";
import CustomTitle from "../../CustomTitle";
import DeleteModal from "../../DeleteModal";
import StyledButtonV2 from "../../StyledButtonV2";
import UpsertFeeScheduleModal from "./UpsertFeeScheduleModal";
import { useInsurancePayerList } from "../../../../queries/use-insurance-payer";
import { NonNullableDate } from "../../../../utils/date-time-utility";

const FeeSchedule = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [curFormState, setCurFormState] = useState<string>();
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [selectedcptHcpcsCode, setSelectedptHcpcsCode] = useState<any>();
  const [searchCptHcpcsKey, setSearchCptHcpcsKey] = useDebounce<string>("");
  const [searchScheduleType, setSearchScheduleType] =
    useState<any>("PAYER_SPECIFIC");
  const [selectedPayers, setSelectedPayers] = useState<any[]>([]);
  const [searchPayerKey, setSearchPayerKey] = useDebounce<any>(undefined);
  const [selectedProvider, setSelectedProvider] = useState<any[]>([]);
  const [searchProviderKey, setSearchProviderKey] = useDebounce<string>("");
  const [payerPagination, setPayerPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [providerPagination, setProviderPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [cptPagination, setCptPagination] =
    useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { mutateAsync: updateFeeScheduleStatus } = useFeeScheduleStatus();

  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const {
    data: insurancePayerList,
    isLoading,
    isFetching: isFetchingPayer,
  } = useInsurancePayerList({
    searchKey: searchPayerKey,
    enabled: searchScheduleType === "PAYER_SPECIFIC",
    pagination: payerPagination,
  });

  const {
    data: feeScheduleList,
    isLoading: tableDataLoading,
    isRefetching: tableRefetching,
    refetch,
  } = useFeeScheduleList({
    pagination,
    gridSorting,
    archive: false,
    status: statusFilter === "all" ? undefined : statusFilter,
    medicalCodeUuid: selectedcptHcpcsCode?.value,
    providerUuid: selectedProvider?.map((it) => it?.value),
    payerId: selectedPayers?.map((it) => it?.value),
    xTENANTID: getXtenantId(),
    feeScheduleType: searchScheduleType,
  });

  const { data: cptHcpcsCodes, isFetching: isFetchingCpt } =
    useFeeCptHcpcsCodeList({
      pagination: cptPagination,
      active: true,
      searchString: searchCptHcpcsKey,
      xTENANTID: getXtenantId(),
    });

  const {
    data: providersList,
    isLoading: providerLoading,
    isFetching: providerFetching,
  } = useProviderListV2({
    status: true,
    pagination: providerPagination,
    searchString: searchProviderKey,
    xTENANTID: getXtenantId(),
  });

  const providerOptions = useMemo(() => {
    const list =
      providersList?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.name,
      })) ?? [];
    const missing = (selectedProvider ?? []).filter(
      (s) => !list.some((o) => o.value === s?.value)
    );
    return [...missing, ...list];
  }, [providersList?.data?.content, selectedProvider]);

  const payerOptions = useMemo(() => {
    const list =
      insurancePayerList?.data?.content?.map((insurance) => ({
        value: insurance.payerId,
        label: insurance.payerName,
      })) ?? [];
    const missing = (selectedPayers ?? []).filter(
      (s) => !list.some((o) => o.value === s?.value)
    );
    return [...missing, ...list];
  }, [insurancePayerList?.data?.content, selectedPayers]);

  const transformedCptHcpcsCodes = useMemo(
    () =>
      cptHcpcsCodes?.data?.content?.map((it) => ({
        value: it?.uuid,
        label: it?.code + " - " + it?.description,
      })),
    [cptHcpcsCodes?.data?.content]
  );

  function toggleStatus<T>(value: boolean, id: T) {
    return updateFeeScheduleStatus({
      feeScheduleId: id?.toString() ?? "",
      status: value,
      xTENANTID: getXtenantId() ?? undefined,
    }).then((res) => {
      refetch();
      return res;
    });
  }

  const isPayerSpecific = searchScheduleType === "PAYER_SPECIFIC";
  const isProviderSpecific = searchScheduleType === "PROVIDER_SPECIFIC";

  const COLUMNS = applyDefaultColumnSettings<any>(
    [
      {
        field: "medicalCodes",
        flex: 0.8,
        minWidth: 180,
        disableColumnMenu: true,
        headerName: "Procedure Code",
        renderCell: ({ value }) => {
          const codes = Object.values?.(value ?? {});

          return <RenderWithTooltip value={codes?.length ? codes?.[0] : "-"} />;
        },
      },
      // {
      //   field: "type",
      //   flex: 0.4,
      //   minWidth: 120,
      //   headerName: "Code Type",
      //   renderCell: ({ value }) => value || "-",
      // },
      ...(!searchScheduleType
        ? [
            {
              field: "feeScheduleType",
              flex: 0.5,
              minWidth: 160,
              headerName: "Schedule Type",
              renderCell: ({ value }: any) =>
                value === "PAYER_SPECIFIC"
                  ? "Payer Specific"
                  : value === "PROVIDER_SPECIFIC"
                    ? "Provider Specific"
                    : "-",
            },
          ]
        : []),
      {
        field: "providers",
        flex: 0.6,
        minWidth: 210,
        disableColumnMenu: true,
        headerName: "Provider",
        renderCell: ({ value }) => {
          const provider = Object?.values(value ?? {});

          return (
            <Box>
              <Text isTabletext>
                {provider?.length ? (provider?.[0] as any) : "-"}
              </Text>
              {/* <Text sx={{ fontSize: "14px", color: "#1A1A1A99" }}>{}</Text> */}
            </Box>
          );
        },
      },
      ...(isPayerSpecific
        ? [
            {
              field: "payers",
              flex: 0.6,
              minWidth: 210,
              disableColumnMenu: true,
              headerName: "Payers",
              renderCell: ({ value }: any) => {
                const payers = Object?.values(value ?? {});
                return (
                  <RenderWithTooltip
                    value={payers?.length ? payers.join(", ") : "-"}
                  />
                );
              },
            },
          ]
        : []),
      {
        field: "modifier",
        headerName: "Modifiers",
        flex: 0.5,
        minWidth: 160,
        renderCell: ({ value }) =>
          value ? Object.values(value).join(", ") : "-",
      },
      {
        field: "amount",
        headerName: isPayerSpecific
          ? "Billed Charge"
          : isProviderSpecific
            ? "Charge Amount"
            : "Amount",
        flex: 0.4,
        minWidth: 160,
        renderCell: ({ value }) => (value ? `$ ${value}` : "-"),
      },
      ...(isPayerSpecific
        ? [
            {
              field: "allowedAmount",
              headerName: "Allowed Amount",
              flex: 0.4,
              minWidth: 160,
              renderCell: ({ value }: any) => (value ? `$ ${value}` : "-"),
            },
          ]
        : []),
      {
        field: "fromDate",
        headerName: "From Date",
        flex: 0.4,
        minWidth: 130,
        renderCell: ({ value }) => NonNullableDate(value),
      },
      {
        field: "endDate",
        headerName: "End Date",
        flex: 0.4,
        minWidth: 130,
        renderCell: ({ value }) => NonNullableDate(value),
      },
      ...(isPayerSpecific
        ? [
            {
              field: "createdBy",
              headerName: "Created By",
              flex: 0.5,
              minWidth: 160,
              renderCell: ({ value }: any) => (
                <RenderWithTooltip value={value || "-"} />
              ),
            },
            {
              field: "createdDate",
              headerName: "Created Date",
              flex: 0.4,
              minWidth: 140,
              renderCell: ({ value }: any) => NonNullableDate(value),
            },
          ]
        : []),
      {
        field: "updatedBy",
        headerName: "Last Updated By",
        flex: 0.5,
        minWidth: 160,
        renderCell: ({ value }) => <RenderWithTooltip value={value || "-"} />,
      },
      {
        field: "updatedDate",
        headerName: "Last Updated Date",
        flex: 0.4,
        minWidth: 140,
        renderCell: ({ value }) => NonNullableDate(value),
      },
      {
        field: "active",
        headerName: "Status",
        flex: 0.3,
        minWidth: 100,
        renderCell: ({ row, value }) => (
          <ToggleSwitch
            id={row.uuid.toString()}
            checked={value}
            content={`You want to ${
              value ? "deactivate" : "activate"
            } this CPT code?`}
            onConfirm={(v, id) => toggleStatus(v, id)}
          />
        ),
      },
      {
        field: "Action",
        width: 90,
        hideSortIcons: true,
        renderCell: ({ row }) => {
          return (
            <ActionMenu
              options={[
                // View and Delete actions removed; only Edit remains
                // { value: "view", label: "View" },
                { value: "edit", label: "Edit" },
                // { value: "delete", label: "Delete" },
              ]}
              onActionClick={(v) => {
                if (v === "delete") {
                  deleteDis.open(row.uuid);
                  return;
                }

                const medicalCodes = transformMultiSelectFrontEndVal(
                  DEFAULT_VALUE_LABEL_LIST,
                  row?.medicalCodes
                )?.[0];

                const medicalCodeObj = medicalCodes
                  ? {
                      ...medicalCodes,
                      label:
                        medicalCodes.label +
                        " - " +
                        (row?.medicalCodeDescription || ""),
                    }
                  : undefined;

                const transformedRow = {
                  ...row,
                  provider: transformMultiSelectFrontEndVal(
                    DEFAULT_VALUE_LABEL_LIST,
                    row?.provider
                  )?.[0],
                  medicalCodes: medicalCodeObj,
                  // modifier is a raw { uuid: label } object; the modal
                  // transforms it itself, so pass it through untouched
                  // modifier: row?.modifier?.split(","),
                };

                feeSchedule.open(transformedRow);
                setCurFormState(v);
              }}
            />
          );
        },
      },
    ],
    ["amount"]
  );

  const [sendMailDisclosure, feeSchedule, deleteDis] = useReduceDisclosure(3);

  const { mutateAsync: archieveFeeSchedule } = useFeeScheduleArchive();

  return (
    <ElevatedPaper>
      <DeleteModal
        w="380px"
        open={deleteDis.opened}
        onClose={deleteDis.close}
        id={deleteDis?.data}
        onConfirmation={(id) =>
          archieveFeeSchedule({
            feeScheduleId: id,
            status: true,
            xTENANTID: getXtenantId(),
          }).then((res) => {
            refetch();
            return res;
          })
        }
        content={"You want to delete Billing ?"}
      />

      <Box>
        <Flex width={"100%"} justifyContent={"space-between"}>
          <Flex width={"100%"} justifyContent={"space-between"} gap={2} mr={2}>
            <CustomTitle>Fee Schedule</CustomTitle>
            {!!rowSelectionModel?.length && (
              <Flex gap={2} alignItems={"center"}>
                <Text>{`${rowSelectionModel.length} Selected`}</Text>
                <Flex gap={2}>
                  <Button
                    variant="outlined"
                    sx={{
                      width: "80px",
                    }}
                    onClick={() => setRowSelectionModel([])}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="contained"
                    sx={{ width: "148px" }}
                    onClick={() => {
                      const filteredPatients = feeScheduleList?.data?.content
                        ?.filter((it) => rowSelectionModel?.includes(it?.uuid))
                        .map((it) => ({
                          uuid: it?.uuid,
                          name: `${it?.firstName || ""}  ${it?.lastName || ""}`,
                        }));

                      sendMailDisclosure.open(filteredPatients);
                    }}
                  >
                    Send Emails
                  </Button>
                </Flex>
              </Flex>
            )}
          </Flex>

          <Flex gap={2} alignItems={"center"}>
            <Box width={"10rem"}>
              {/* 
            <CustomSelect
              value={statusFilter as any}
              label=""
              placeholder="Status"
              options={}
              onChange={(v) => setStatusFilter(v as any)}
            /> */}

              <FilterSelect
                value={statusFilter}
                required
                placeholder="Select"
                name="active"
                options={STATUS as any}
                onChange={(value) => setStatusFilter(value)}
              />
            </Box>

            <Box width={"13rem"}>
              <FilterSelect
                value={searchScheduleType}
                required
                placeholder="Schedule Type"
                name="scheduleType"
                options={
                  [
                    {
                      value: "PROVIDER_SPECIFIC",
                      label: "Provider Specific",
                    },
                    {
                      value: "PAYER_SPECIFIC",
                      label: "Payer Specific",
                    },
                  ] as any
                }
                onChange={(value) => setSearchScheduleType(value)}
              />
            </Box>

            {isPayerSpecific && (
              <FilterMaskMultiSelectScroll
                name="payers"
                loading={isLoading || isFetchingPayer}
                optionWithCheckbox
                value={selectedPayers}
                onChange={(value) => setSelectedPayers(value ?? [])}
                onSearch={(v) => {
                  setSearchPayerKey(v);
                  setPayerPagination((p) => ({ ...p, page: 0 }));
                }}
                options={payerOptions}
                placeholder="Select Payer"
                pagination={payerPagination}
                setPagination={setPayerPagination}
                totalElements={insurancePayerList?.data?.page?.totalElements}
              />
            )}

            {(isPayerSpecific || isProviderSpecific) && (
              <FilterMaskMultiSelectScroll
                name="providers"
                optionWithCheckbox
                placeholder="Select Provider"
                onSearch={(v) => {
                  setSearchProviderKey(v);
                  setProviderPagination((p) => ({ ...p, page: 0 }));
                }}
                loading={providerLoading || providerFetching}
                options={providerOptions}
                value={selectedProvider}
                onChange={(val) => setSelectedProvider(val ?? [])}
                pagination={providerPagination}
                setPagination={setProviderPagination}
                totalElements={providersList?.data?.page?.totalElements}
              />
            )}

            <Box width={"20rem"}>
              <AsyncSelectScroll
                isMatchButtonHeight
                placeholder="Select CPT/ HCPCS Procedure Code"
                onSearch={(v) => {
                  setSearchCptHcpcsKey(v);
                  setCptPagination((p) => ({ ...p, page: 0 }));
                }}
                name="diagnosis"
                options={transformedCptHcpcsCodes}
                value={selectedcptHcpcsCode}
                onChange={(opt) => setSelectedptHcpcsCode(opt)}
                loading={isFetchingCpt}
                pagination={cptPagination}
                setPagination={setCptPagination}
                totalElements={cptHcpcsCodes?.data?.page?.totalElements}
              />
            </Box>

            {feeSchedule.opened && (
              <UpsertFeeScheduleModal
                curFormState={curFormState}
                disclosureHost={feeSchedule}
              />
            )}

            <StyledButtonV2
              label={"Add Fee Schedule"}
              onClick={() => feeSchedule.open()}
              shouldAddIcon
            />
          </Flex>
        </Flex>

        {sendMailDisclosure.opened && (
          <SendBulkEmailModal
            disClosureHost={sendMailDisclosure}
            onSave={() => setRowSelectionModel([])}
          />
        )}

        <GlobalTableHeightWrapper
          height="calc(100vh -  8rem)"
          sx={{ overflowY: "auto" }}
        >
          <CustomDataGridV2
            rows={feeScheduleList?.data?.content ?? []}
            getRowId={(row) => row?.uuid}
            rowCount={feeScheduleList?.data.page.totalElements}
            loading={tableDataLoading || tableRefetching}
            columns={COLUMNS}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={setRowSelectionModel}
            onSortModelChange={setGridSorting}
            sortModel={gridSorting}
          />
        </GlobalTableHeightWrapper>
      </Box>
    </ElevatedPaper>
  );
};

export default FeeSchedule;
